import { useFocusTheme } from "../theme/FocusThemeContext";
import usePageTitle from "../hooks/usePageTitle";

// Internal design-review tool, not public content — no NavBar entry. Stands
// in for a design tool per ROADMAP's Phase 1 decision ("design happens in
// code"): renders every current token, the ad-hoc type scale, the two real
// button styles, the card component and the two section-header patterns on
// one page, in whatever focus state is active. Combine with the `?focus=`
// override (e.g. /styleguide?focus=Night) to see all 7 states — full page
// loads only, since FocusThemeProvider only reads the query string once, on
// its own mount.
const FOCUS_STATES = ["Dawn", "Morning", "Noon", "Dusk", "Night", "Sleep", "None"];

// Color tokens as declared per html[data-focus] block in global.css — swatch
// against the live value so drift between this list and the CSS is visible
// at a glance, not just read as a claim.
const COLOR_TOKENS = [
  { name: "--bg", note: "the one page color" },
  { name: "--text", note: "ink" },
  { name: "--accent", note: "bronze on warm themes, ink on neutral ones" },
  { name: "--accent-strong", note: "hover/active accent" },
  { name: "--text-muted", note: "rgba(--text-rgb, var(--a-muted))" },
  { name: "--text-faint", note: "rgba(--text-rgb, var(--a-faint))" },
  { name: "--border", note: "rgba(--text-rgb, var(--a-hair))" },
  { name: "--border-strong", note: "rgba(--text-rgb, var(--a-hair) + 0.14)" },
  { name: "--card-text", note: "= var(--text) — cards tint the page, not a fixed color" },
];

// Static (non-theme) tokens — same across all 7 states.
const STATIC_RAMP = ["--accent-200", "--accent-400", "--accent-600"];
const RADII = ["--radius-s", "--radius-m"];
const SHADOWS = ["--shadow-1", "--shadow-2"];

export default function Styleguide() {
  usePageTitle("Styleguide");
  const { focus } = useFocusTheme();

  return (
    <main className="styleguide">
      <header className="sg-header">
        <p className="sg-kicker">/styleguide — internal, not linked from the nav</p>
        <h1>Design system snapshot</h1>
        <p className="sg-intro">
          Every token, the current (pre-redesign) type scale, the two real
          button styles, the card component and both section-header patterns
          in use today — in one place, so Phase 1's hierarchy pass has a
          single page to check instead of one section at a time. Current
          focus state: <strong>{focus}</strong>. Full page load to preview
          another:
        </p>
        <nav className="sg-focus-switch" aria-label="Preview other focus states">
          {FOCUS_STATES.map((state) => (
            <a key={state} href={`/styleguide?focus=${state}`}>
              {state}
            </a>
          ))}
        </nav>
      </header>

      <section className="sg-block">
        <p className="sg-block-label">Color tokens</p>
        <div className="sg-swatch-grid">
          {COLOR_TOKENS.map(({ name, note }) => (
            <div className="sg-swatch" key={name}>
              <div
                className="sg-swatch-fill"
                style={{ background: `var(${name})` }}
              />
              <code>{name}</code>
              <span>{note}</span>
            </div>
          ))}
        </div>

        <p className="sg-block-label">Static accent ramp (unthemed)</p>
        <div className="sg-swatch-grid">
          {STATIC_RAMP.map((name) => (
            <div className="sg-swatch" key={name}>
              <div
                className="sg-swatch-fill"
                style={{ background: `var(${name})` }}
              />
              <code>{name}</code>
            </div>
          ))}
        </div>

        <p className="sg-block-label">Radii &amp; shadows</p>
        <div className="sg-swatch-grid sg-swatch-grid--wide">
          {RADII.map((name) => (
            <div className="sg-swatch" key={name}>
              <div
                className="sg-swatch-fill sg-swatch-fill--radius"
                style={{ borderRadius: `var(${name})` }}
              />
              <code>{name}</code>
            </div>
          ))}
          {SHADOWS.map((name) => (
            <div className="sg-swatch" key={name}>
              <div
                className="sg-swatch-fill sg-swatch-fill--shadow"
                style={{ boxShadow: `var(${name})` }}
              />
              <code>{name}</code>
            </div>
          ))}
        </div>
      </section>

      <section className="sg-block">
        <p className="sg-block-label">
          Type scale — values duplicated here deliberately as a snapshot of
          what ships today; Phase 1 item 2 (rework the scale) should update
          this block too, not just global.css
        </p>
        <div className="sg-type-row">
          <p className="sg-sample sg-sample--eyebrow">Eyebrow label</p>
          <code>.section-label — 0.7rem / 600 / 0.35em, used by Projects + ProjectPage only</code>
        </div>
        <div className="sg-type-row">
          <p className="sg-sample sg-sample--hero">Hero</p>
          <code>.landing h1 — clamp(2.2rem, 6.5vw, 5rem) / 800 / -0.04em (MATCHED PAIR with .splash-title)</code>
        </div>
        <div className="sg-type-row">
          <p className="sg-sample sg-sample--heading">Section heading</p>
          <code>.education h2 / .contact h2 — clamp(2rem, 5vw, 3.5rem) / 800 / -0.03em</code>
        </div>
        <div className="sg-type-row">
          <p className="sg-sample sg-sample--card-title">Card title</p>
          <code>.project-card h3 — 1.6rem / 700 / -0.02em</code>
        </div>
        <div className="sg-type-row">
          <p className="sg-sample sg-sample--body">
            Body copy sits at the weight the focus state needs — 300 on dark
            fields, 400 on the light ones (Noon/Dusk) — so thin strokes never
            go muddy on a light background.
          </p>
          <code>.card-desc / .pp-body p — 0.85rem (card) or 1rem+ (body) / var(--weight-body)</code>
        </div>
        <div className="sg-type-row">
          <p className="sg-sample sg-sample--meta">Meta / small text</p>
          <code>.chip, .filter-btn, nav links — 0.7–0.75rem / 500–600</code>
        </div>
      </section>

      <section className="sg-block">
        <p className="sg-block-label">
          Buttons — the two interactive pill styles that exist today; there is
          no primary/CTA button anywhere on the site yet
        </p>
        <div className="sg-row">
          <button className="filter-btn">Filter (default)</button>
          <button className="filter-btn selected">Filter (selected)</button>
          <span className="chip">Chip / tag</span>
        </div>
      </section>

      <section className="sg-block">
        <p className="sg-block-label">Card</p>
        <div className="sg-row">
          <div className="project-card sg-card-demo">
            <div
              className="card-art"
              style={{
                backgroundImage:
                  "linear-gradient(200deg, rgba(var(--text-rgb), 0.14) 0%, rgba(var(--text-rgb), 0.02) 72%)",
              }}
            />
            <div className="card-body">
              <p className="card-category">Mobile</p>
              <h3>Sample card</h3>
              <p className="card-desc">
                Same markup as a real project card — `.project-card` is
                unscoped so it drops in here unchanged.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sg-block">
        <p className="sg-block-label">
          Section headers — two different patterns currently live side by
          side; worth deciding on one before the redesign, not after
        </p>
        <div className="sg-row sg-row--wrap">
          <div>
            <p className="section-label">Eyebrow pattern (Projects)</p>
            <p className="sg-sample--note">
              No heading follows it directly — the eyebrow sits above the
              project grid, not above a big title.
            </p>
          </div>
          <div>
            <h2 className="sg-sample sg-sample--heading" style={{ margin: 0 }}>
              Plain H2 (Education / Contact)
            </h2>
          </div>
        </div>
      </section>
    </main>
  );
}
